/**
 * SeoTasksPage — `/seo/tasks` (E1). Единый приоритизированный бэклог находок раздела.
 * Апрув (new→approved) / dismiss — роль seo|admin (curator предлагает).
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { Button, LoadingBlock, ErrorBlock, Badge } from '@/shared/ui';
import { MetaBadges } from '@/components/seo/MetaBadges';
import { useSeoTasks, useSeoMutations, type SeoTask } from '@/hooks/api/useSeo';
import { useAuth } from '@/auth/useAuth';

const STATUS_TONE: Record<string, string> = {
  new: 'b-info', approved: 'b-success', in_progress: 'b-violet', done: 'b-neutral', dismissed: 'b-warning',
};

export function SeoTasksPage() {
  usePageHeader({ title: 'SEO — Задачи', subtitle: 'Бэклог находок и апрув-контур', actions: <HelpButton help={SEO_HELP.tasks} /> });
  const { user } = useAuth();
  const canApprove = user?.role === 'seo' || user?.role === 'admin';
  const [status, setStatus] = useState('');
  const { data, isLoading, isError, error, refetch } = useSeoTasks(status ? { status } : {});
  const { patchTask } = useSeoMutations();

  const tasks = data?.tasks ?? [];

  return (
    <div className="col gap20" style={{ maxWidth: 1150 }}>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <select className="input" style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Все статусы</option>
          <option value="new">Новые</option>
          <option value="approved">Апрувнутые</option>
          <option value="in_progress">В работе</option>
          <option value="done">Сделано</option>
          <option value="dismissed">Отклонённые</option>
        </select>
        {patchTask.isError && <span className="muted" style={{ fontSize: 12.5, color: 'var(--danger, #DC2626)' }}>{patchTask.error?.message}</span>}
      </div>

      {isLoading && <LoadingBlock label="Загружаю бэклог…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {data && (
        tasks.length === 0 ? (
          <div className="card"><p className="muted" style={{ margin: 0 }}>Задач нет. Находки появляются из аудитов, карты кластеров и замера.</p></div>
        ) : (
          <section className="card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Задача</th>
                  <th>Модуль</th>
                  <th>Метки</th>
                  <th>Статус</th>
                  <th style={{ textAlign: 'right' }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <TaskRow key={t.id} t={t} canApprove={canApprove} onPatch={(s) => patchTask.mutate({ id: t.id, status: s })} pending={patchTask.isPending} />
                ))}
              </tbody>
            </table>
          </section>
        )
      )}
    </div>
  );
}

function TaskRow({ t, canApprove, onPatch, pending }: { t: SeoTask; canApprove: boolean; onPatch: (s: string) => void; pending: boolean }) {
  return (
    <tr>
      <td>
        <div className="col" style={{ gap: 3 }}>
          <span style={{ fontWeight: 600 }}>{t.title}</span>
          {t.description && <span className="muted" style={{ fontSize: 12 }}>{t.description}</span>}
          {t.approved_by && <span className="muted mono" style={{ fontSize: 11 }}>апрув: @{t.approved_by}</span>}
        </div>
      </td>
      <td><span className="muted" style={{ fontSize: 12.5 }}>{t.source_module}</span></td>
      <td><MetaBadges confidence={t.confidence} impact={t.impact} effort={t.effort} engine={t.engine} /></td>
      <td><Badge tone={STATUS_TONE[t.status] ?? 'b-neutral'}>{t.status}</Badge></td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {t.status === 'new' && canApprove && (
          <>
            <Button size="sm" variant="primary" onClick={() => onPatch('approved')} disabled={pending}>Апрув</Button>{' '}
            <Button size="sm" variant="outline" onClick={() => onPatch('dismissed')} disabled={pending}>Отклонить</Button>
          </>
        )}
        {t.status === 'approved' && (
          <Button size="sm" variant="outline" onClick={() => onPatch('in_progress')} disabled={pending}>В работу</Button>
        )}
        {t.status === 'in_progress' && (
          <Button size="sm" variant="outline" onClick={() => onPatch('done')} disabled={pending}>Готово</Button>
        )}
        {t.status === 'new' && !canApprove && <span className="muted" style={{ fontSize: 12 }}>апрув — seo/admin</span>}
      </td>
    </tr>
  );
}
